import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import { translateTeamToAbbr } from '../utils/teamAbbreviations';

const MyEntries = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editPicks, setEditPicks] = useState([]);
  const [editName, setEditName] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const formatDeadlineDisplay = (deadlineStr) => {
    if (!deadlineStr) return 'Invalid';
    try {
      let dateString = deadlineStr;
      // If no timezone info, treat as HST (-10:00)
      if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
        dateString = dateString + '-10:00';
      }
      const dt = new Date(dateString);
      if (isNaN(dt.getTime())) return 'Invalid Date';
      const options = {
        timeZone: 'Pacific/Honolulu',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      return dt.toLocaleString('en-US', options);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const fetchData = async () => {
    try {
      const [entriesRes, weeksRes] = await Promise.all([
        api.get(`/entries/my-entries/${user?.id}`),
        api.get('/weeks'),
      ]);
      setEntries(entriesRes.data);
      setWeeks(weeksRes.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getWeekInfo = (weekId) => {
    return weeks.find((w) => w.id === weekId);
  };

  const handleEdit = async (entry) => {
    setEditingEntry(entry);
    setEditPicks(entry.picks);
    setEditName(entry.entry_name);
    
    try {
      const response = await api.get('/weeks');
      const weekData = response.data.find(w => w.id === entry.week_id);
      if (weekData) {
        const updatedWeeks = weeks.map(w => 
          w.id === entry.week_id ? { ...w, games: weekData.games } : w
        );
        setWeeks(updatedWeeks);
      }
    } catch (error) {
      console.error('Error fetching week games:', error);
    }
  };

  const handleUpdate = async () => {
    if (!editingEntry) return;

    try {
      await api.put(
        `/entries/${editingEntry.id}?user_id=${user?.id}&username=${user?.username}`,
        {
          week_id: editingEntry.week_id,
          entry_name: editName,
          picks: editPicks,
        }
      );
      await fetchData();
      setEditingEntry(null);
      alert('Entry updated successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update entry');
    }
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!entryToDelete) return;

    try {
      await api.delete(`/entries/${entryToDelete.id}?user_id=${user?.id}`);
      await fetchData();
      setShowDeleteModal(false);
      setEntryToDelete(null);
      alert('Entry deleted successfully!');
    } catch (error) {
      setShowDeleteModal(false);
      alert(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <h1 className="text-3xl font-bold text-primary mb-6">My Entries</h1>

        {entries.length === 0 ? (
          <div className="bg-dark-secondary rounded-lg p-6 text-center">
            <p className="text-gray-300 text-xl">No entries found</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {entries.map((entry) => {
              const weekInfo = getWeekInfo(entry.week_id);
              return (
                <div key={entry.id} className="bg-dark-secondary rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-white mb-1">
                        {entry.entry_name}
                      </h2>
                      <p className="text-gray-400">
                        Week {weekInfo?.week_number} - {weekInfo?.season_year}
                      </p>
                      <p className="text-gray-500 text-sm">
                        Submitted: {formatDeadlineDisplay(entry.submitted_at)} HST
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(entry)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(entry)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {entry.picks.map((pick, index) => (
                      <div
                        key={index}
                        className="bg-dark px-4 py-2 rounded-lg text-center"
                      >
                        <span className="text-gray-400 text-sm">Pick {index + 1}</span>
                        <p className="text-white font-semibold">{translateTeamToAbbr(pick)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showDeleteModal && entryToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-dark-secondary rounded-lg p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold text-white mb-4">⚠️ Confirm Delete</h2>
              <p className="text-gray-300 mb-2">
                Are you sure you want to delete this entry?
              </p>
              <div className="bg-dark rounded-lg p-4 mt-4">
                <p className="text-white font-semibold mb-1">{entryToDelete.entry_name}</p>
                <p className="text-gray-400 text-sm">
                  Week {getWeekInfo(entryToDelete.week_id)?.week_number} - {getWeekInfo(entryToDelete.week_id)?.season_year}
                </p>
              </div>
              <p className="text-red-400 text-sm mt-4">
                This action cannot be undone.
              </p>
              <div className="mt-6 space-y-3">
                <button
                  onClick={confirmDelete}
                  className="w-full bg-red-600 text-white py-3 rounded-lg font-bold hover:bg-red-700 transition"
                >
                  Yes, Delete Entry
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setEntryToDelete(null);
                  }}
                  className="w-full bg-dark text-gray-300 py-3 rounded-lg font-bold hover:bg-gray-700 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {editingEntry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-dark-secondary rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-white mb-4">Edit Entry (v2)</h2>
              
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Entry Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full bg-dark text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-300 mb-4">
                  Select Your 6 Teams ({editPicks.length}/6 selected)
                </label>
                <div className="space-y-4">
                  {(() => {
                    const currentWeek = editingEntry ? weeks.find(w => w.id === editingEntry.week_id) : null;
                    
                    // Helper function to get opposite spread
                    const getOppositeSpread = (spread) => {
                      if (!spread) return '';
                      const numericValue = parseFloat(spread);
                      if (isNaN(numericValue)) return '';
                      const opposite = -numericValue;
                      return opposite > 0 ? `+${opposite}` : `${opposite}`;
                    };

                    const handlePickTeam = (team) => {
                      if (editPicks.includes(team)) {
                        // Remove team
                        setEditPicks(editPicks.filter(p => p !== team));
                      } else {
                        // Add team (max 6)
                        if (editPicks.length < 6) {
                          setEditPicks([...editPicks, team]);
                        }
                      }
                    };
                    
                    return currentWeek?.games?.map((game, index) => (
                      <div key={index} className="bg-dark rounded-lg p-4">
                        <div className="flex items-center justify-between gap-3">
                          <button
                            onClick={() => handlePickTeam(game.team1)}
                            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition ${
                              editPicks.includes(game.team1)
                                ? 'bg-primary text-white'
                                : 'bg-dark-secondary text-gray-300 hover:bg-gray-700'
                            }`}
                          >
                            {game.team1} {game.spread}
                          </button>
                          
                          <span className="text-gray-400 font-bold text-sm">vs</span>
                          
                          <button
                            onClick={() => handlePickTeam(game.team2)}
                            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition ${
                              editPicks.includes(game.team2)
                                ? 'bg-primary text-white'
                                : 'bg-dark-secondary text-gray-300 hover:bg-gray-700'
                            }`}
                          >
                            {game.team2} {getOppositeSpread(game.spread)}
                          </button>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleUpdate}
                  className="flex-1 bg-primary text-white py-3 rounded-lg font-bold hover:bg-green-600 transition"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setEditingEntry(null)}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-lg font-bold hover:bg-gray-700 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyEntries;
